import { ownFootprintDrops } from "../document/footprint-cells.ts";
import { planReplace, scanReplace, type ReplacePlacement, type ReplacePlan, type ReplaceScope } from "./replace.ts";
import { planReplaceCells, scanReplaceCells, type ReplaceCellPicks } from "./replace-cells.ts";
import type { ReplaceChoice, ReplaceEditPlan, ReplaceEntry, ReplaceHost, ReplaceListing, ReplaceMergeOffer, ReplaceOptions, ReplaceOutcome, ReplacePropPreview, ReplaceSkipped, ReplaceTerrainPreview } from "./replace-edits-types.ts";
import { mergeParts, mergeRefusalReason, planReplaceMerge, type MergeEnv, type ReplaceMergePlan } from "./replace-merge.ts";
import { planReplaceProps, scanReplaceProps, type ReplacePropEnv, type ReplacePropPlan, type ReplacePropSkip } from "./replace-props.ts";

/**
 * Replace in selection in the store (ADR-097): whether the dialog is open, what it lists for the choices
 * made in it, and Apply. The area is the tile selection's rectangle; with no selection it is the whole active
 * layer, and only once that is asked for. The layers are the active layer or, with a selection, every tile,
 * terrain, collision, zone and prop layer the tools may edit (ADR-097's amendment of 2026-10-12, decisions 8
 * and 12); door layers are never read (decision 11). A locked or hidden layer is left out when the replace is planned and
 * named, since the store drops a whole command that touches one. Apply plans against the map as it stands
 * and runs one command, so one undo takes the replace back. A whole-layer terrain replace may instead be merged
 * into a layer that already paints the entry (decisions 26 to 28), when the choice asks for it.
 */

/**
 * Where a replace reads: the area, the layers it may write and the layers it skips.
 */
interface Reach {
  readonly area: "selection" | "layer";
  readonly scope: ReplaceScope;
  readonly skipped: readonly ReplaceSkipped[];
}

const EMPTY: ReplaceListing = { area: "none", rect: null, sources: [], skipped: [], entries: [], brushes: [], zoneTypes: [] };

/**
 * The layer kinds a replace reads.
 */
const REPLACED_KINDS: ReadonlySet<string> = new Set(["tile", "terrain", "collision", "zone", "prop"]);

/**
 * The parts of a replace planned: the one command and its counts, or null when nothing would change, and the
 * props of a ticked type left for want of a frame for their states.
 */
interface ReplaceParts {
  readonly plan: ReplaceEditPlan | null;
  readonly propsSkipped: readonly ReplacePropSkip[];
}

/**
 * The helper.
 */
export class ReplaceEdits {
  private shown = false;

  /**
   * @param host The store's view of itself.
   */
  public constructor(private readonly host: ReplaceHost) {}

  /**
   * Whether the dialog is open. View state, never saved and outside history.
   *
   * @returns True while it is open.
   */
  public get open(): boolean {
    return this.shown;
  }

  /**
   * Why the dialog cannot open, or null when it can: it needs an active tile, terrain, collision, zone or prop
   * layer.
   *
   * @returns The reason, or null.
   */
  public refusal(): string | null {
    const active = this.host.map().layers.find((layer) => layer.id === this.host.activeLayerId());
    if (active === undefined) {
      return "No active layer";
    }
    return REPLACED_KINDS.has(active.kind) ? null : "Only on a tile, terrain, collision, zone or prop layer";
  }

  /**
   * What a prop replace reads and asks of the editor.
   *
   * @returns The frame sizes, the tile size, the frame each prop is shown with and the game's suggestions.
   */
  private propEnv(): ReplacePropEnv {
    return { sizeOf: this.host.frameSize, tileSize: this.host.world().tileSize, frameOf: this.host.shownFrame, stateFrame: this.host.suggestedStateFrame, animations: this.host.suggestedAnimations };
  }

  /**
   * Opens the dialog.
   *
   * @returns False when it is refused or already open.
   */
  public show(): boolean {
    if (this.shown || this.refusal() !== null) {
      return false;
    }
    this.shown = true;
    this.host.notify();
    return true;
  }

  /**
   * Closes the dialog; subscribers hear of it only when it was open.
   */
  public close(): void {
    if (this.shown) {
      this.shown = false;
      this.host.notify();
    }
  }

  /**
   * What the dialog lists for the choices made in it.
   *
   * @param options Where to replace.
   * @returns The listing; empty with no selection until the whole layer is asked for.
   */
  public listing(options: ReplaceOptions): ReplaceListing {
    const reach = this.reach(options);
    if (reach === null) {
      return EMPTY;
    }
    const entries = this.host.terrains().flatMap((terrain) => (terrain.type === "corner" ? terrain.colors.map((colour): ReplaceEntry => ({ terrain: terrain.id, colour: colour.index, name: this.host.terrainName(terrain.id, colour.index) })) : []));
    const map = this.host.map();
    return {
      area: reach.area,
      rect: reach.scope.rect,
      sources: [...scanReplace(map, reach.scope, this.host.world().tilesets), ...scanReplaceCells(map, reach.scope), ...scanReplaceProps(map, reach.scope, this.propEnv())],
      skipped: reach.skipped,
      entries,
      brushes: this.host.collisionBrushes(),
      zoneTypes: this.host.zoneTypes(),
    };
  }

  /**
   * Plans the replace against the map as it stands, over the layers the listing reads: the tiles and terrain,
   * then the collision values and zone types, then the props, in one compound. The cell fills write only
   * collision and zone layers and the prop updates only prop layers, which no tile or terrain part touches, so
   * they are planned on the map as it stands.
   *
   * @param options Where to replace.
   * @param choice What was ticked.
   * @returns The plan, or null when nothing would change.
   * @throws Error when tiles are ticked with no tile in hand, props with no prop in hand, the entry names a
   *   terrain the editor does not hold or a colour it lacks, the layer the result is placed against is gone,
   *   the collision value is no brush's, the zone type is not registered or the prop in hand names a tileset
   *   the world does not hold.
   */
  public plan(options: ReplaceOptions, choice: ReplaceChoice): ReplaceEditPlan | null {
    return this.parts(options, choice).plan;
  }

  /**
   * Plans every part of the replace and joins them into the one compound.
   *
   * @param options Where to replace.
   * @param choice What was ticked.
   * @returns The plan, or null when nothing would change, and the props a ticked type left.
   * @throws Error as `plan` does.
   */
  private parts(options: ReplaceOptions, choice: ReplaceChoice): ReplaceParts {
    const reach = this.reach(options);
    if (reach === null) {
      return { plan: null, propsSkipped: [] };
    }
    const merge = this.planMerge(reach, choice);
    const base = this.planTilesAndTerrain(reach, merge === null ? choice : { ...choice, terrain: [] });
    const cells = planReplaceCells(this.host.world(), this.host.map().id, reach.scope, this.cellPicks(choice));
    const props = this.planProps(reach, choice);
    const propsSkipped = props?.skipped ?? [];
    const propCommand = props?.command ?? null;
    if (cells === null && propCommand === null && merge === null) {
      return { plan: base === null ? null : { ...base, collision: 0, zones: 0, props: 0, propsSkipped, mergedInto: null }, propsSkipped };
    }
    const commands = base === null ? [] : base.command.kind === "compound" ? base.command.commands : [base.command];
    const propCommands = propCommand === null ? [] : propCommand.kind === "compound" ? propCommand.commands : [propCommand];
    return {
      plan: {
        command: { kind: "compound", commands: [...commands, ...(merge?.command.commands ?? []), ...(cells?.command.commands ?? []), ...propCommands] },
        cells: base?.cells ?? 0,
        corners: (base?.corners ?? 0) + (merge?.corners ?? 0),
        collision: cells?.collision ?? 0,
        zones: cells?.zones ?? 0,
        props: props?.props ?? 0,
        propsSkipped,
        layers: [...(base?.layers ?? []), ...(merge?.layers ?? []), ...(cells?.layers ?? []), ...(props?.layers ?? [])],
        made: base?.made ?? [],
        unresolved: (base?.unresolved ?? 0) + (merge?.unresolved ?? 0),
        cutAbove: base?.cutAbove ?? null,
        mergedInto: merge?.layers[0] ?? null,
      },
      propsSkipped,
    };
  }

  /**
   * What a merge reads and asks of the editor.
   *
   * @returns The unpainted layers' colours, why a layer refuses edits, a prop's art and a terrain layer's
   *   resolved tiles.
   */
  private mergeEnv(): MergeEnv {
    return { colourOf: (layerId) => this.host.layerColour(layerId), blocked: (layerId) => this.blocked(layerId), sizeOf: this.host.frameSize, tileSize: this.host.world().tileSize, frameOf: this.host.shownFrame, resolved: this.host.resolvedTiles };
  }

  /**
   * The merge a terrain replace may run instead of keeping two layers of one entry, read from the map as it
   * stands; nothing is run.
   *
   * @param options Where to replace.
   * @param terrain The terrain layers ticked.
   * @param to The entry they become.
   * @returns The layer they would be merged into and why the merge may not run, or null when there is no merge
   *   to offer: no area, no ticked layer that would change, a cut, or no other layer of the entry.
   */
  public mergeOffer(options: ReplaceOptions, terrain: readonly string[], to: { readonly terrain: string; readonly colour: number }): ReplaceMergeOffer | null {
    const reach = this.reach(options);
    const parts = reach === null ? null : mergeParts(this.host.map(), reach.scope, this.host.world().tilesets, terrain, to, this.mergeEnv());
    return parts === null ? null : { target: parts.target, refused: parts.refused, layers: parts.sources.length };
  }

  /**
   * Plans the merge part of a replace, on the map as it stands: the tile part writes only tile layers, so it
   * leaves the terrain layers and the map's order as the merge reads them.
   *
   * @param reach Where the replace reads.
   * @param choice What was ticked.
   * @returns The merge's plan, or null when the choice asks for none.
   * @throws Error when a merge is asked for and there is no layer to merge into, the merge is refused (its
   *   reason, naming the layer), or the entry names a terrain the editor does not hold.
   */
  private planMerge(reach: Reach, choice: ReplaceChoice): ReplaceMergePlan | null {
    const to = choice.terrainTo;
    if (choice.merge !== true || to === null || choice.terrain.length === 0) {
      return null;
    }
    const parts = mergeParts(this.host.map(), reach.scope, this.host.world().tilesets, choice.terrain, to, this.mergeEnv());
    if (parts === null) {
      throw new Error("no layer to merge into");
    }
    if (parts.refused !== null) {
      throw new Error(mergeRefusalReason(this.host.map(), parts.refused));
    }
    const terrain = this.host.terrains().find((t) => t.id === to.terrain);
    if (terrain === undefined) {
      throw new Error(`terrain ${to.terrain} is not in the compile context`);
    }
    return planReplaceMerge(this.host.world(), this.host.map().id, parts.sources, parts.target, { terrain, colour: to.colour, name: this.host.terrainName(to.terrain, to.colour) });
  }

  /**
   * Plans the prop part of a replace: the ticked frames and types take the prop in hand.
   *
   * @param reach Where the replace reads.
   * @param choice What was ticked.
   * @returns The prop plan, or null when no prop is ticked or nothing would change or be skipped.
   * @throws Error when props are ticked with no prop in hand or the prop in hand names a tileset the world
   *   does not hold.
   */
  private planProps(reach: Reach, choice: ReplaceChoice): ReplacePropPlan | null {
    const frames = choice.props ?? [];
    const types = choice.propTypes ?? [];
    if (frames.length === 0 && types.length === 0) {
      return null;
    }
    const to = choice.propTo ?? null;
    if (to === null) {
      throw new Error("no prop in hand");
    }
    return planReplaceProps(this.host.world(), this.host.map().id, reach.scope, { frames, types, to }, this.propEnv());
  }

  /**
   * The collision and zone part of a choice, checked against what the game offers: a kind with nothing ticked
   * or nothing chosen picks nothing.
   *
   * @param choice What was ticked.
   * @returns The picks.
   * @throws Error when the collision value is no brush's or the zone type is not registered.
   */
  private cellPicks(choice: ReplaceChoice): ReplaceCellPicks {
    const collisionTo = choice.collisionTo ?? null;
    const zoneTo = choice.zoneTo ?? null;
    if (collisionTo !== null && !this.host.collisionBrushes().some((brush) => brush.value === collisionTo)) {
      throw new Error(`no collision brush holds the value ${String(collisionTo)}`);
    }
    if (zoneTo !== null && !this.host.zoneTypes().some((type) => type.id === zoneTo)) {
      throw new Error(`zone type ${zoneTo} is not registered`);
    }
    const collision = choice.collision ?? [];
    const zones = choice.zones ?? [];
    return {
      collision: collisionTo === null || collision.length === 0 ? null : { from: collision, to: collisionTo },
      zones: zoneTo === null || zones.length === 0 ? null : { from: zones, to: zoneTo },
    };
  }

  /**
   * Plans the tile and terrain part of a replace.
   *
   * @param reach Where the replace reads.
   * @param choice What was ticked.
   * @returns The plan, or null when no tile or terrain would change.
   * @throws Error when tiles are ticked with no tile in hand, the entry names a terrain the editor does not
   *   hold or a colour it lacks, or the layer the result is placed against is gone.
   */
  private planTilesAndTerrain(reach: Reach, choice: ReplaceChoice): ReplacePlan | null {
    const hand = this.host.tileInHand();
    if (choice.tiles.length > 0 && hand === null) {
      throw new Error("no tile in hand");
    }
    const to = choice.terrainTo;
    const terrain = to === null ? undefined : this.host.terrains().find((t) => t.id === to.terrain);
    if (to !== null && terrain === undefined) {
      throw new Error(`terrain ${to.terrain} is not in the compile context`);
    }
    const entry = to === null || terrain === undefined ? null : { terrain, colour: to.colour, name: this.host.terrainName(to.terrain, to.colour) };
    return planReplace(
      this.host.world(),
      this.host.map().id,
      reach.scope,
      {
        tiles: hand === null ? [] : choice.tiles.map((from) => ({ from, to: hand })),
        terrain: entry === null ? [] : choice.terrain.map((from) => ({ from, to: entry })),
        placement: choice.placement ?? { kind: "keep" },
      },
      { colourOf: (layerId) => this.host.layerColour(layerId), usable: (layerId) => this.blocked(layerId) === null, newLayerId: () => this.host.newLayerId() },
    );
  }

  /**
   * What the dialog says before a terrain replace, read from the map as it stands; nothing is run.
   *
   * @param options Where to replace.
   * @param terrain The terrain layers ticked.
   * @param to The entry they become.
   * @param placement Where the result is placed; kept in place by default.
   * @returns Whether the area cuts through a ticked layer's paint, whether a cut's layer ends above its
   *   source, the other layers that already paint the entry, and the cells the entry has no tile for; all
   *   empty when the entry cannot be planned.
   */
  public terrainPreview(options: ReplaceOptions, terrain: readonly string[], to: { readonly terrain: string; readonly colour: number }, placement: ReplacePlacement = { kind: "keep" }): ReplaceTerrainPreview {
    const reach = this.reach(options);
    if (reach === null || terrain.length === 0) {
      return { cuts: false, above: null, existing: [], unresolved: 0 };
    }
    const map = this.host.map();
    const inArea = new Map(scanReplace(map, reach.scope, this.host.world().tilesets).flatMap((source) => (source.kind === "terrain" ? [[source.layerId, source.count] as const] : [])));
    const cuts = map.layers.some((layer) => {
      const taken = inArea.get(layer.id);
      return layer.kind === "terrain" && terrain.includes(layer.id) && taken !== undefined && layer.corners.reduce((sum, colour) => sum + (colour === 0 ? 0 : 1), 0) > taken;
    });
    const existing = map.layers.filter((layer) => layer.kind === "terrain" && layer.terrain === to.terrain && !terrain.includes(layer.id) && this.host.layerColour(layer.id) === to.colour).map((layer) => layer.id);
    let unresolved = 0;
    let above: boolean | null = null;
    try {
      const plan = this.plan(options, { tiles: [], terrain, terrainTo: to, placement });
      unresolved = plan?.unresolved ?? 0;
      above = plan?.cutAbove ?? null;
    } catch {
      unresolved = 0;
    }
    return { cuts, above, existing, unresolved };
  }

  /**
   * What the dialog says before a prop replace, read from the map as it stands; nothing is run. A plan of
   * props left alone is kept here, where `plan` is null for want of anything to change.
   *
   * @param options Where to replace.
   * @param choice The prop frames and types ticked and the prop in hand.
   * @returns How many props will change and the props that will be left, or null with no area, no prop
   *   ticked, no prop in hand, or a prop in hand the replace cannot take.
   */
  public propPreview(options: ReplaceOptions, choice: Pick<ReplaceChoice, "props" | "propTypes" | "propTo">): ReplacePropPreview | null {
    const reach = this.reach(options);
    const ticked = (choice.props ?? []).length + (choice.propTypes ?? []).length > 0;
    if (reach === null || !ticked || (choice.propTo ?? null) === null) {
      return null;
    }
    try {
      const plan = this.planProps(reach, { tiles: [], terrain: [], terrainTo: null, ...choice });
      return { props: plan?.props ?? 0, skipped: plan?.skipped ?? [] };
    } catch {
      return null;
    }
  }

  /**
   * Runs the replace as one command and closes the dialog when there was something to replace, then selects
   * the props of a ticked type it left for want of a frame for their states, so the author can find them, and
   * makes the layer a merge went into active when the merge removed the active layer.
   * Ticked tiles with no tile in hand and ticked props with no prop in hand run nothing.
   *
   * @param options Where to replace.
   * @param choice What was ticked.
   * @returns What it did.
   */
  public apply(options: ReplaceOptions, choice: ReplaceChoice): ReplaceOutcome {
    if (choice.tiles.length > 0 && this.host.tileInHand() === null) {
      return { kind: "empty hand" };
    }
    if ((choice.props ?? []).length + (choice.propTypes ?? []).length > 0 && (choice.propTo ?? null) === null) {
      return { kind: "no prop in hand" };
    }
    const skipped = this.reach(options)?.skipped ?? [];
    const { plan, propsSkipped } = this.parts(options, choice);
    if (plan === null) {
      this.selectLeft(propsSkipped);
      return propsSkipped.length === 0 ? { kind: "nothing" } : { kind: "nothing", propsSkipped: propsSkipped.length };
    }
    this.shown = false;
    if (!this.host.run(plan.command)) {
      this.host.notify();
      return { kind: "refused" };
    }
    this.selectLeft(propsSkipped);
    if (plan.mergedInto !== null && this.host.activeLayerId() === null) {
      this.host.setActiveLayer(plan.mergedInto);
    }
    const dropped = ownFootprintDrops(plan.command);
    return { kind: "replaced", cells: plan.cells, corners: plan.corners, collision: plan.collision, zones: plan.zones, props: plan.props, propsSkipped: propsSkipped.length, ...(dropped === 0 ? {} : { footprintsDropped: dropped }), layers: plan.layers.length, made: plan.made.length, unresolved: plan.unresolved, skipped, ...(plan.mergedInto === null ? {} : { mergedInto: plan.mergedInto }) };
  }

  /**
   * Selects the props a replace left, when it left any; the selection is otherwise kept.
   *
   * @param left The props left.
   */
  private selectLeft(left: readonly ReplacePropSkip[]): void {
    if (left.length > 0) {
      this.host.select(left.map((skip) => ({ layerId: skip.layerId, propId: skip.propId })));
    }
  }

  /**
   * Why the tools may not edit a layer.
   *
   * @param layerId The layer.
   * @returns The reason, or null when they may.
   */
  private blocked(layerId: string): "locked" | "hidden" | null {
    return this.host.isLocked(layerId) ? "locked" : this.host.isHidden(layerId) ? "hidden" : null;
  }

  /**
   * Where a replace reads for the choices made: the selection's rectangle over the active layer or every
   * tile, terrain, collision, zone and prop layer, or the whole active layer; a layer that refuses edits is
   * left out and, when it holds something in the area, named.
   *
   * @param options Where to replace.
   * @returns The reach, or null with no selection while the whole layer has not been asked for.
   */
  private reach(options: ReplaceOptions): Reach | null {
    const rect = this.host.selection();
    if (rect === null && !options.wholeLayer) {
      return null;
    }
    const map = this.host.map();
    const all = rect !== null && options.layers === "all";
    const active = this.host.activeLayerId();
    const tilesets = this.host.world().tilesets;
    const layerIds: string[] = [];
    const skipped: ReplaceSkipped[] = [];
    for (const layer of map.layers) {
      if (!REPLACED_KINDS.has(layer.kind) || !(all || layer.id === active)) {
        continue;
      }
      const why = this.blocked(layer.id);
      const alone = { rect, layerIds: [layer.id] };
      if (why === null) {
        layerIds.push(layer.id);
      } else if (scanReplace(map, alone, tilesets).length > 0 || scanReplaceCells(map, alone).length > 0 || scanReplaceProps(map, alone, this.propEnv()).length > 0) {
        skipped.push({ layerId: layer.id, why });
      }
    }
    return { area: rect === null ? "layer" : "selection", scope: { rect, layerIds }, skipped };
  }
}
